/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import React, { useEffect, useState } from "react"
import { ConfirmCancelButtons } from "../../Components/Buttons/ConfirmCancelButtons"
import { useConfirmation } from "../../hooks/useConfirmation"

import { Button, Modal, Spacer, Table, TextGroupMedium } from "@cd2/components"
import { Form2 } from "@cd2/forms/build/es6"
import { P } from "@cd2/text/build/es6"
import { IComponentProps } from "@cd2/tools"
import { RadioButtonSelect } from "../../Components/FieldsRadioCheckbox"
import { useGlobalState } from "../../Components/GlobalStateProvider"
import { useAsyncEffect } from "../../hooks/useAsyncEffect"
import { IRecord } from "../../types/schema"
import { fetchPricesForCustomer } from "../../utils/fetchPricesForCustomer"
import { calculatePriceFromMargin, formatPrice } from "../../utils/utils"
import { calculateMarginFromPrice, IItem, itemTypes } from "./quoteOrderUtils"
import {
  groupPriceConverted,
  useProductGroupProductPrice,
} from "../../hooks/productGroupProductPrice"
import { calculateCostPriceFromCurrency } from "./ItemRowSageProduct"
import { convertPriceCurrency } from "../../utils/currency"

type IProduct = IRecord<
  "products",
  "cost_price_pennies" | "body" | "stock_code" | "sage_id" | "active"
> & {
  suppliers: IRecord<"suppliers", "currency_name">[]
  preferred_supplier?: IRecord<"suppliers", "currency_name">
  product_group: IRecord<"product_groups", "products_price_method">
  isKeyAccount: boolean
}

export function useConfirmCostPriceUpdate(customerId?: string) {
  const skeem = window.skeem
  const getGroupCostPrice = useProductGroupProductPrice()
  const { currentUser } = useGlobalState()
  return useConfirmation<
    {
      currencyName: string
      items: IItem[]
      extraButtons?: (
        onResolve: (data: { items: IItem[] | null | false; option?: "1" | "2" | "3" }) => void,
        isLoading: boolean,
      ) => IComponentProps<typeof Button>[]
    },
    { items: IItem[] | null | false; option?: "1" | "2" | "3" }
  >(({ data, onResolve }) => {
    const { items } = data
    const [productsData, setProductsData] = useState<
      (IProduct & { customerPrice?: number; groupPrice?: number | null })[]
    >([])
    const dataItems = items.filter(item => {
      const product = productsData.find(p => p.id === item.product?.id)
      if (product?.groupPrice) {
        return product.groupPrice !== item.cost_price_pennies
      }
      return product && product.cost_price_pennies !== item.cost_price_pennies
    })
    async function loadProductsData() {
      try {
        const products = await skeem.fetch2<
          (IProduct & {
            customerPrice?: number
            groupPrice?: number | null
            branch: IRecord<"branches", "currency_name">
          })[]
        >({
          model: {
            name: "products",
            attributes: [
              "cost_price_pennies",
              "body",
              "stock_code",
              "sage_id",
              "active",
              { name: "suppliers", attributes: ["currency_name"] },
              { name: "preferred_supplier", attributes: ["currency_name"] },
              { name: "product_group", attributes: ["products_price_method"] },
              { name: "branch", attributes: ["currency_name"] },
              {
                as: "isKeyAccount",
                value: { exists: { path: ["product_master", "universal_price_band_products"] } },
              },
            ],
            filter: {
              matches: {
                id: { or: items.filter(v => !!v.product).map(v => v.product!.id) },
              },
            },
          },
        })
        const groupPricedItems = await Promise.all(
          products.map(product => getGroupCostPrice(product.id)),
        )
        for (const product of products) {
          const groupPrice = groupPricedItems.find(v => v?.original_product_id === product.id)
          if (groupPrice) {
            product.groupPrice = groupPriceConverted(
              product,
              groupPrice,
              currentUser as any,
              data.currencyName,
            )
          }
          product.cost_price_pennies = calculateCostPriceFromCurrency({
            currentUser,
            product,
            currency_name: data.currencyName,
          })
          if (customerId) {
            const customerPrices = await fetchPricesForCustomer(skeem, product.id, customerId)
            let pricing: undefined | (typeof customerPrices)[number]
            if (customerPrices) {
              const universalPrices = customerPrices.filter(
                price => price.sourceType === "universal_price_band",
              )
              const sharedPrices = customerPrices.filter(
                price => price.sourceType === "shared_price_band",
              )
              if (universalPrices.length === 1) {
                pricing = universalPrices[0]
              } else if (sharedPrices.length === 1) {
                pricing = sharedPrices[0]
              } else if (customerPrices.length === 1) {
                pricing = customerPrices?.[0]
              } else if (customerPrices.length === 2) {
                const nonStandardCustomerPrices = customerPrices.filter(
                  price => price.name !== "Standard",
                )
                if (nonStandardCustomerPrices.length === 1) {
                  pricing = nonStandardCustomerPrices[0]
                }
              }
            }
            if (pricing) {
              product.customerPrice =
                convertPriceCurrency(
                  pricing.price_pennies,
                  currentUser.branch.euro_pound_exchange_rate,
                  pricing.currency_name,
                  data.currencyName,
                ) ?? undefined
            }
          }
        }
        setProductsData(products)
      } catch (e) {
        console.error(e)
      }
    }

    useAsyncEffect(async () => {
      await loadProductsData()
    }, [JSON.stringify(dataItems.map(v => v.product?.id))])

    function handleSubmit({ option }: { option: "1" | "2" | "3" }) {
      if (option === "1") {
        // Update prices, maintain margin
        const updatedItems: IItem[] = []
        productsData
          .filter(x => !x.isKeyAccount)
          .forEach(product => {
            const items = dataItems.filter(item => item.product?.id === product.id)
            updatedItems.push(
              ...items.map(item => ({
                ...item,
                cost_price_pennies: product.groupPrice ?? product.cost_price_pennies ?? 0,
                price_pennies: calculatePriceFromMargin(
                  product.groupPrice ?? product.cost_price_pennies ?? 0,
                  item.markUp ?? 0,
                ),
              })),
            )
          })
        onResolve({ items: updatedItems, option: "1" })
        return
      }
      if (option === "2") {
        // Update product price with price list price
        const updatedItems: IItem[] = []
        productsData.forEach(product => {
          const items = dataItems.filter(item => item.product?.id === product.id)
          updatedItems.push(
            ...items.map(item => ({
              ...item,
              cost_price_pennies: product.groupPrice ?? product.cost_price_pennies ?? 0,
              price_pennies: product.customerPrice ?? item.price_pennies ?? 0,
              markUp: calculateMarginFromPrice(
                product.groupPrice ?? product.cost_price_pennies ?? 0,
                product.customerPrice ?? item.price_pennies ?? 0,
              ),
            })),
          )
        })
        onResolve({ items: updatedItems, option: "2" })
        return
      }
      if (option === "3") {
        // Keep price, update cost price
        const updatedItems: IItem[] = []
        productsData.forEach(product => {
          const items = dataItems.filter(item => item.product?.id === product.id)
          updatedItems.push(
            ...items.map(item => ({
              ...item,
              cost_price_pennies: product.groupPrice ?? product.cost_price_pennies ?? 0,
              markUp: calculateMarginFromPrice(
                product.groupPrice ?? product.cost_price_pennies ?? 0,
                item.price_pennies ?? 0,
              ),
            })),
          )
        })
        onResolve({ items: updatedItems, option: "3" })
        return
      }
      onResolve({ items: null })
    }

    return (
      <Modal
        width={800}
        render={() => (
          <Form2.Form onSubmit={handleSubmit} initialData={{ option: "1" }}>
            <Form2.WithField<"1" | "2" | "3" | undefined>
              field="option"
              render={optionField => {
                const option = optionField.getValue()
                const option1DataItems = dataItems.filter(
                  item => !productsData.find(v => v.id === item.product?.id)?.isKeyAccount,
                )
                useEffect(() => {
                  if (dataItems.length && !option1DataItems.length && option === "1") {
                    optionField.setValue("2")
                  }
                }, [option1DataItems])
                return (
                  <React.Fragment>
                    <TextGroupMedium
                      heading="Update prices"
                      body="Some cost prices have changed, please select how to update the prices for the products."
                    />
                    <React.Fragment>
                      <RadioButtonSelect
                        field="option"
                        optionDisabled={opt =>
                          (opt.value === "2" && !customerId) ||
                          (opt.value === "1" && option1DataItems.length === 0)
                        }
                        options={[
                          {
                            label: "Update prices and maintain margins",
                            description: "excludes key account products",
                            value: "1",
                          },
                          {
                            label: "Update product price with price list price",
                            value: "2",
                            description: !customerId
                              ? "Select a customer to use price list price"
                              : "Prices will only be set if a price list price is available.",
                          },
                          {
                            label: "Keep prices the same, update cost prices only",
                            value: "3",
                          },
                        ]}
                      />
                      <Spacer />

                      {/*  Update prices, maintain margin */}
                      {option === "1" && (
                        <React.Fragment>
                          <P weight={600}>Preview the changes below</P>
                          <div
                            css={css`
                              max-height: 400px;
                              overflow-x: hidden;
                              overflow-y: auto;
                            `}
                          >
                            <PreviewTable1
                              data={option1DataItems.map(item => {
                                const product = productsData.find(v => v.id === item.product?.id)
                                return {
                                  ...item,
                                  new_price_pennies:
                                    item.type === itemTypes.buildSetItem
                                      ? 0
                                      : calculatePriceFromMargin(
                                          (product
                                            ? product.groupPrice ?? product.cost_price_pennies
                                            : item.cost_price_pennies) ?? 0,
                                          item.markUp ?? 0,
                                        ),
                                  new_cost_price_pennies:
                                    (product
                                      ? product.groupPrice ?? product.cost_price_pennies
                                      : item.cost_price_pennies) ?? 0,
                                }
                              })}
                            />
                          </div>
                        </React.Fragment>
                      )}
                      {/* // Update price with price list price */}
                      {option === "2" && (
                        <React.Fragment>
                          <P weight={600}>Preview the changes below</P>
                          <div
                            css={css`
                              max-height: 400px;
                              overflow-x: hidden;
                              overflow-y: auto;
                            `}
                          >
                            <PreviewTable2
                              data={dataItems
                                .map(item => {
                                  const product = productsData.find(v => v.id === item.product?.id)
                                  return {
                                    ...item,
                                    customerPrice:
                                      item.type === itemTypes.buildSetItem
                                        ? 0
                                        : product?.customerPrice ?? 0,
                                    new_markUp:
                                      item.type === itemTypes.buildSetItem
                                        ? 0
                                        : calculateMarginFromPrice(
                                            product?.groupPrice ?? product?.cost_price_pennies ?? 0,
                                            product?.customerPrice ?? item.price_pennies ?? 0,
                                          ),
                                    new_cost_price_pennies:
                                      (product
                                        ? product.groupPrice ?? product.cost_price_pennies
                                        : item.cost_price_pennies) ?? 0,
                                  }
                                })
                                .filter(item => !!item.customerPrice)}
                            />
                          </div>
                        </React.Fragment>
                      )}
                      {/* // Keep price, update cost price */}
                      {option === "3" && (
                        <React.Fragment>
                          <P weight={600}>Preview the changes below</P>
                          <div
                            css={css`
                              max-height: 400px;
                              overflow-x: hidden;
                              overflow-y: auto;
                            `}
                          >
                            <PreviewTable3
                              data={dataItems.map(item => {
                                const product = productsData.find(v => v.id === item.product?.id)
                                return {
                                  ...item,
                                  new_cost_price_pennies:
                                    (product
                                      ? product.groupPrice ?? product.cost_price_pennies
                                      : item.cost_price_pennies) ?? 0,
                                  new_markUp:
                                    item.type === itemTypes.buildSetItem
                                      ? 0
                                      : calculateMarginFromPrice(
                                          (product
                                            ? product.groupPrice ?? product.cost_price_pennies
                                            : item.cost_price_pennies) ?? 0,
                                          item.price_pennies ?? 0,
                                        ),
                                }
                              })}
                            />
                          </div>
                        </React.Fragment>
                      )}

                      <Spacer />
                      <ConfirmCancelButtons
                        onConfirm={"submit"}
                        buttonOpts={{ confirm: { children: "Confirm Changes" } }}
                        onCancel={() => onResolve({ items: false })}
                      />
                    </React.Fragment>
                  </React.Fragment>
                )
              }}
            />
          </Form2.Form>
        )}
        isOpen
      />
    )
  })
}

function PreviewTable1({
  data,
}: {
  data: (IItem & {
    new_price_pennies: number | null
    new_cost_price_pennies: number | null
  })[]
}) {
  const { currency } = useGlobalState()
  return (
    <Table.HorizontalOverflow minWidth={600}>
      <Table data={data}>
        <Table.DataColumn
          heading="Product"
          field="product"
          render={product => product.stock_code}
        />
        <Table.Column heading="Cost Price">
          {row => (
            <div>
              <i>{formatPrice(row.cost_price_pennies, { isPennies: true, currency })}</i> to{" "}
              <b>{formatPrice(row.new_cost_price_pennies, { isPennies: true, currency })}</b>
            </div>
          )}
        </Table.Column>
        <Table.Column heading="Price">
          {row => (
            <div>
              <i>{formatPrice(row.price_pennies, { isPennies: true, currency })}</i> to{" "}
              <b>{formatPrice(row.new_price_pennies, { isPennies: true, currency })}</b>
            </div>
          )}
        </Table.Column>
      </Table>
    </Table.HorizontalOverflow>
  )
}

function PreviewTable2({
  data,
}: {
  data: (IItem & { customerPrice: number | null; new_markUp: number | null })[]
}) {
  const { currency } = useGlobalState()
  return (
    <Table.HorizontalOverflow minWidth={600}>
      <Table data={data}>
        <Table.DataColumn
          heading="Product"
          field="product"
          render={product => product.stock_code}
        />
        <Table.Column heading="Cost Price">
          {row => (
            <div>
              <i>{formatPrice(row.cost_price_pennies, { isPennies: true, currency })}</i> to{" "}
              <b>{formatPrice(row.new_cost_price_pennies, { isPennies: true, currency })}</b>
            </div>
          )}
        </Table.Column>
        <Table.Column heading="Price">
          {row => (
            <div>
              <i>{formatPrice(row.price_pennies, { isPennies: true, currency })}</i> to{" "}
              <b>{formatPrice(row.customerPrice, { isPennies: true, currency })}</b>
            </div>
          )}
        </Table.Column>
        <Table.Column heading="Margin">
          {row => (
            <div>
              <i>{row.markUp}</i> to <b>{row.new_markUp}</b>
            </div>
          )}
        </Table.Column>
      </Table>
    </Table.HorizontalOverflow>
  )
}

function PreviewTable3({
  data,
}: {
  data: (IItem & {
    new_cost_price_pennies: number | null
    new_markUp: number | null
  })[]
}) {
  const { currency } = useGlobalState()
  return (
    <Table.HorizontalOverflow minWidth={600}>
      <Table data={data}>
        <Table.DataColumn
          heading="Product"
          field="product"
          render={product => product.stock_code}
        />
        <Table.Column heading="Cost Price">
          {row => (
            <div>
              <i>{formatPrice(row.cost_price_pennies, { isPennies: true, currency })}</i> to{" "}
              <b>{formatPrice(row.new_cost_price_pennies, { isPennies: true, currency })}</b>
            </div>
          )}
        </Table.Column>
        <Table.Column heading="Margin">
          {row => (
            <div>
              <i>{row.markUp}</i> to <b>{row.new_markUp}</b>
            </div>
          )}
        </Table.Column>
      </Table>
    </Table.HorizontalOverflow>
  )
}
